/**
 * useCompetitions - the Competition table, cached for the session.
 *
 * One fetch feeds the wizard dropdown, the card and calendar colours, the
 * details eyebrow and the IPTC preview. Rows change a few times a year, so
 * a long staleTime is right; the manage tab invalidates the key on every
 * write so a new competition shows up in the wizard without a reload.
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCompetitions } from '../../../api';

export const COMPETITIONS_QUERY_KEY = ['competitions'];

const EMPTY = [];

export default function useCompetitions() {
  const { data, isLoading, error } = useQuery({
    queryKey: COMPETITIONS_QUERY_KEY,
    queryFn: () => fetchCompetitions(),
    staleTime: 10 * 60 * 1000,
  });
  const competitions = data ?? EMPTY;
  const bySlug = useMemo(
    () => Object.fromEntries(competitions.map((c) => [c.slug, c])),
    [competitions],
  );
  const active = useMemo(() => competitions.filter((c) => c.active), [competitions]);
  return { competitions, active, bySlug, isLoading, error };
}

/** Drop the cached list after a write on the manage tab. */
export function useInvalidateCompetitions() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: COMPETITIONS_QUERY_KEY });
}
